import type { ISourceFileDetails } from '@/types/feedSourceTypes'
import { useSourcesStore } from '@/stores/sourcesStore'

export const getSourcesStats = async (
  endpoint: string,
  accountId: string,
): Promise<ISourceFileDetails[]> => {
  // Get available sources from cache
  const sourcesStore = useSourcesStore()
  const availableSources = sourcesStore.sources
    .filter((source) => source.type === 'feed')
    .map((source) => source.id)

  if (availableSources.length === 0) {
    console.warn('getSourcesStats: No sources available in cache')
    return []
  }

  const stats: ISourceFileDetails[] = []

  for (const source of availableSources) {
    const path = `reports/import-feed-file-url?jazelId=${accountId}&importProductIds=${source}`
    try {
      const res = await fetch(`${endpoint}/${path}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!res.ok) {
        console.warn(`getSourcesStats: no data for ${source} (status ${res.status})`)
        continue
      }

      // if the API returns an empty payload you may also want to `continue` here
      stats.push(await res.json())
    } catch {
      // ignore and move on
    }
  }

  return stats.flat()
}

export const getSourceStats = async (
  endpoint: string,
  accountId: string,
  importProductId: string,
): Promise<ISourceFileDetails | null> => {
  const path = `reports/import-feed-file-url?jazelId=${encodeURIComponent(accountId)}&importProductIds=${encodeURIComponent(importProductId)}`

  try {
    const res = await fetch(`${endpoint}/${path}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })

    if (!res.ok) {
      console.warn(`getSourcesStats: no data for ${importProductId} (status ${res.status})`)
      return null
    }

    const resJson = await res.json()

    if (resJson.length === 0) {
      console.warn(`getSourcesStats: no data for ${importProductId} (empty response)`)
      return null
    }

    return resJson?.[0] ?? null
  } catch (err) {
    console.error('getSourcesStats error:', err)
    return null
  }
}

export const getSourcesProcessDetails = async (
  endpoint: string,
  accountId: string,
): Promise<any> => {
  // Get available sources from cache
  const sourcesStore = useSourcesStore()
  const availableSources = sourcesStore.sources
    .filter((source) => source.type === 'feed')
    .map((source) => source.id)

  if (availableSources.length === 0) {
    console.warn('getSourcesProcessDetails: No sources available in cache')
    return []
  }

  const stats: any[] = []

  for (const source of availableSources) {
    const path = `reports/import-feed-processing-details?jazelId=${accountId}&importProductIds=${source}`
    try {
      const res = await fetch(`${endpoint}/${path}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!res.ok) {
        console.warn(`getSourcesStats: no data for ${source} (status ${res.status})`)
        continue
      }

      // if the API returns an empty payload you may also want to `continue` here
      stats.push(await res.json())
    } catch {
      // ignore and move on
    }
  }

  return stats.flat()
}

export const getAllProcessingJobs = async (endpoint: string, accountId: string): Promise<any[]> => {
  const path = `check-import-feed-status`
  try {
    const response = await fetch(`${endpoint}/${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jazelId: accountId,
        days: 7,
        limit: 2000,
      }),
    })

    if (!response.ok) {
      console.error('Error fetching all processing jobs')
      return []
    }

    const data = await response.json()
    return Array.isArray(data?.jobs) ? data.jobs : []
  } catch (error) {
    console.error('Error fetching all processing jobs:', error)
    return []
  }
}

export const getTodayProcessingJobs = async (
  endpoint: string,
  accountId: string,
): Promise<any[]> => {
  const allJobs = await getAllProcessingJobs(endpoint, accountId)

  const now = new Date()
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime()

  return allJobs.filter((job: any) => {
    const timestamp = job.updatedAt || job.createdAt
    if (!timestamp) return false
    const ts = new Date(timestamp).getTime()
    return ts >= startOfDay && ts < endOfDay && job.status === 'SUCCEEDED'
  })
}
